(() => {
  const configElement = document.getElementById('ls-quote-config');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  // Shopify HTML-escapes translations (We'll -> We&#39;ll); this copy is set as
  // text, so it is decoded once here.
  const copy = Object.fromEntries(Object.entries(config.strings).map(([key, value]) => [key, decode(value)]));
  const storageKey = `annapurna-quote-v1:${config.currency}`;
  const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
  const page = document.querySelector('[data-ls-quote-list]');
  const form = page?.querySelector('[data-ls-quote-form]');
  const downloadButton = page?.querySelector('[data-ls-quote-download]');
  const refreshButton = page?.querySelector('[data-ls-quote-refresh]');
  const clearButton = page?.querySelector('[data-ls-quote-clear]');
  const maxQuantity = 999999;
  const validHandle = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(value);
  const validImage = (value) => typeof value === 'string' && /^(https:)?\/\/[^\s"'<>]+$/.test(value);
  let entries = readEntries();
  let items = [];
  let busy = false;
  let revision = 0;
  let pdfModule;
  let quoteFingerprint;
  let quoteReference;
  let emailedReference = '';

  function readEntries() {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(saved)) return [];
      const unique = new Map();
      for (const entry of saved.slice(0, 100)) {
        if (!validHandle(entry.handle) || !/^\d+$/.test(String(entry.variantId))) continue;
        if (!Number.isInteger(entry.quantity) || entry.quantity < 1 || entry.quantity > maxQuantity) continue;
        unique.set(String(entry.variantId), {
          handle: entry.handle,
          variantId: String(entry.variantId),
          quantity: entry.quantity,
        });
      }
      return [...unique.values()];
    } catch {
      return [];
    }
  }

  function saveEntries(next) {
    try {
      if (next.length) localStorage.setItem(storageKey, JSON.stringify(next));
      else localStorage.removeItem(storageKey);
    } catch {
      throw new Error(copy.storage_error);
    }
    entries = next;
    revision += 1;
    updateCounts();
  }

  function linesLabel(count) {
    return (count === 1 ? copy.lines_one : copy.lines_other).replace('[count]', count);
  }

  function updateCounts(bump = false) {
    const count = entries.length;
    document.querySelectorAll('[data-ls-quote-count]').forEach((element) => {
      element.textContent = element.closest('[data-ls-quote-tab]') ? count : `(${count})`;
    });
    document.querySelectorAll('[data-ls-quote-view]').forEach((element) => { element.hidden = count === 0; });
    const tab = document.querySelector('[data-ls-quote-tab]');
    if (tab) {
      tab.hidden = count === 0;
      tab.setAttribute('aria-label', `${copy.tab_label}: ${linesLabel(count)}`);
      if (bump) {
        tab.classList.remove('ls-quote-tab--bump');
        void tab.offsetWidth;
        tab.classList.add('ls-quote-tab--bump');
      }
    }
  }

  function money(cents) {
    return new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency }).format(cents / 100);
  }

  // The data template outputs text parsed out of product HTML, so entities such
  // as &amp; can survive the parse. DOMParser decodes them without running or
  // inserting anything.
  function decode(value) {
    if (typeof value !== 'string' || !value.includes('&')) return typeof value === 'string' ? value : '';
    return new DOMParser().parseFromString(value, 'text/html').documentElement.textContent;
  }

  async function fetchProduct(handle) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=ls-quote-data`, {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
      });
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(copy.load_error);
      const product = await response.json();
      if (!product || !Array.isArray(product.variants)) throw new Error(copy.load_error);
      return product;
    } finally {
      clearTimeout(timeout);
    }
  }

  function itemFromProduct(entry, product) {
    const variant = product?.variants?.find((value) => String(value.id) === entry.variantId);
    if (!variant || !Number.isSafeInteger(variant.price) || variant.price < 0) {
      return { ...entry, title: entry.handle, specs: [], unavailable: true };
    }
    const image = [variant.image, product.image].find(validImage) || '';
    return {
      ...entry,
      title: typeof product.title === 'string' ? product.title : entry.handle,
      variant: variant.title === 'Default Title' ? '' : String(variant.title || ''),
      brand: decode(product.brand),
      specs: (Array.isArray(product.specs) ? product.specs : [])
        .map((spec) => ({ label: decode(spec?.label), value: decode(spec?.value) }))
        .filter((spec) => spec.label && spec.value)
        .slice(0, 4),
      sku: typeof variant.sku === 'string' ? variant.sku : '',
      price: variant.price,
      stock: Number.isInteger(variant.stock) ? variant.stock : null,
      image,
      unavailable: !variant.available,
      url: `${root}products/${encodeURIComponent(entry.handle)}?variant=${entry.variantId}`,
    };
  }

  async function refreshItems() {
    const startRevision = revision;
    const products = new Map();
    const queue = [...new Set(entries.map((entry) => entry.handle))];
    await Promise.all(Array.from({ length: Math.min(4, queue.length) }, async () => {
      while (queue.length) {
        const handle = queue.shift();
        products.set(handle, await fetchProduct(handle));
      }
    }));
    if (startRevision !== revision) return false;
    items = entries.map((entry) => itemFromProduct(entry, products.get(entry.handle)));
    renderItems();
    return true;
  }

  function listStatus(message) {
    const status = page?.querySelector('[data-ls-list-status]');
    if (!status) return;
    status.hidden = !message;
    status.textContent = message;
  }

  function setButtonLabel(button, label) {
    const target = button?.querySelector('[data-ls-button-label]');
    if (target) target.textContent = label;
  }

  function setBusy(value) {
    busy = value;
    if (!page) return;
    page.setAttribute('aria-busy', String(value));
    [refreshButton, clearButton].forEach((button) => { if (button) button.disabled = value; });
    page.querySelectorAll('[data-ls-quote-quantity], [data-ls-quote-remove]').forEach((element) => {
      element.disabled = value;
    });
    items.forEach(updateRow);
    updateActions();
  }

  function updateActions() {
    if (downloadButton) downloadButton.disabled = busy || !items.length || items.some((item) => item.unavailable);
  }

  function stockNote(item) {
    if (item.unavailable || item.stock === null || item.quantity <= item.stock) return '';
    return copy.stock_limited.replace('[count]', Math.max(item.stock, 0));
  }

  function updateRow(item) {
    const row = page.querySelector(`[data-variant-id="${item.variantId}"]`);
    if (!row) return;
    const value = item.unavailable ? null : item.price * item.quantity;
    row.querySelector('[data-ls-item-total]').textContent = value === null ? '-' : money(value);
    const note = row.querySelector('[data-ls-item-note]');
    note.textContent = stockNote(item);
    note.hidden = !note.textContent;
    row.querySelector('[data-ls-qty-step="-1"]').disabled = busy || item.quantity <= 1;
    row.querySelector('[data-ls-qty-step="1"]').disabled = busy || item.quantity >= maxQuantity;
  }

  function updateTotals() {
    let total = 0;
    let units = 0;
    for (const item of items) {
      if (!item.unavailable) total += item.price * item.quantity;
      units += item.quantity;
      updateRow(item);
    }
    page.querySelector('[data-ls-quote-subtotal]').textContent = money(total);
    page.querySelector('[data-ls-summary-lines]').textContent = items.length;
    page.querySelector('[data-ls-summary-units]').textContent = units;
    page.querySelector('[data-ls-quote-lines]').textContent = `(${linesLabel(items.length)})`;
    updateActions();
  }

  function renderItems() {
    const container = page.querySelector('[data-ls-quote-items]');
    const template = page.querySelector('[data-ls-quote-row]');
    container.replaceChildren();
    page.querySelector('[data-ls-quote-empty]').hidden = entries.length > 0;
    page.querySelector('[data-ls-quote-content]').hidden = entries.length === 0;
    for (const item of items) {
      const row = template.content.firstElementChild.cloneNode(true);
      row.dataset.variantId = item.variantId;
      row.classList.toggle('ls-quote-item--unavailable', Boolean(item.unavailable));
      row.querySelectorAll('[data-ls-item-link]').forEach((link) => { if (item.url) link.href = item.url; });
      row.querySelector('[data-ls-item-title]').textContent = item.title;
      const image = row.querySelector('[data-ls-item-image]');
      if (item.image) {
        image.src = item.image;
        image.hidden = false;
      }
      const brand = row.querySelector('[data-ls-item-brand]');
      brand.textContent = item.brand || '';
      brand.hidden = !item.brand;
      const variant = row.querySelector('[data-ls-item-variant]');
      variant.textContent = item.variant || '';
      variant.hidden = !item.variant;
      const specs = row.querySelector('[data-ls-item-specs]');
      for (const spec of item.specs) {
        const chip = document.createElement('span');
        chip.className = 'ls-quote-chip';
        const label = document.createElement('span');
        label.className = 'ls-quote-chip__label';
        label.textContent = spec.label;
        const value = document.createElement('span');
        value.textContent = spec.value;
        chip.append(label, ' ', value);
        specs.append(chip);
      }
      specs.hidden = !item.specs.length;
      row.querySelector('[data-ls-item-price]').textContent = Number.isSafeInteger(item.price) ? copy.each.replace('[price]', money(item.price)) : '';
      const quantity = row.querySelector('[data-ls-quote-quantity]');
      quantity.value = item.quantity;
      quantity.disabled = busy;
      quantity.setAttribute('aria-label', `${copy.quantity}: ${item.title}`);
      row.querySelectorAll('[data-ls-qty-step]').forEach((button) => {
        button.querySelector('.visually-hidden').textContent += `: ${item.title}`;
      });
      const remove = row.querySelector('[data-ls-quote-remove]');
      remove.disabled = busy;
      remove.setAttribute('aria-label', `${copy.remove}: ${item.title}`);
      if (item.unavailable) {
        const error = row.querySelector('[data-ls-item-error]');
        error.hidden = false;
        error.textContent = copy.unavailable;
      }
      container.appendChild(row);
    }
    updateCounts();
    updateTotals();
  }

  function enableProductButtons(scope = document) {
    scope.querySelectorAll('[data-ls-quote-add]').forEach((button) => { button.disabled = false; });
    updateCounts();
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-ls-quote-add]');
    if (!button || button.disabled) return;
    const status = button.closest('[data-ls-quote-product]').querySelector('[data-ls-quote-status]');
    const productForm = document.getElementById(button.dataset.productForm);
    const variantId = productForm?.querySelector('[name="id"]')?.value;
    const quantityInput = productForm?.elements.namedItem('quantity');
    const quantity = quantityInput ? Number(quantityInput.value) : 1;
    if (quantityInput && !quantityInput.reportValidity()) return;
    if (!/^\d+$/.test(String(variantId)) || !Number.isInteger(quantity) || quantity < 1 || quantity > maxQuantity) {
      status.textContent = copy.invalid_quantity;
      return;
    }
    button.disabled = true;
    status.textContent = copy.loading;
    try {
      const product = await fetchProduct(button.dataset.productHandle);
      const entry = { handle: button.dataset.productHandle, variantId: String(variantId), quantity };
      if (itemFromProduct(entry, product).unavailable) throw new Error(copy.unavailable);
      const next = readEntries();
      const existing = next.find((value) => value.variantId === entry.variantId);
      if (existing) {
        if (existing.quantity + quantity > maxQuantity) throw new Error(copy.invalid_quantity);
        existing.quantity += quantity;
      } else {
        if (next.length >= 100) throw new Error(copy.limit_error);
        next.push(entry);
      }
      saveEntries(next);
      updateCounts(true);
      status.textContent = copy.added;
    } catch (error) {
      const known = [copy.storage_error, copy.unavailable, copy.invalid_quantity, copy.limit_error];
      status.textContent = known.includes(error.message) ? error.message : copy.load_error;
    } finally {
      button.disabled = false;
    }
  });

  document.addEventListener('shopify:section:load', (event) => enableProductButtons(event.target));
  document.addEventListener('product-info:loaded', (event) => enableProductButtons(event.target));
  enableProductButtons();

  window.addEventListener('storage', async (event) => {
    if (event.key !== storageKey && event.key !== null) return;
    entries = readEntries();
    revision += 1;
    updateCounts();
    if (page && !busy) await loadPage();
  });

  if (!page) return;

  function updateSticky() {
    const main = page.querySelector('.ls-quote-main');
    if (!main) return;
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 80;
    main.classList.toggle('ls-quote-main--sticky', main.offsetHeight + header + 48 <= window.innerHeight);
  }

  if ('ResizeObserver' in window) new ResizeObserver(updateSticky).observe(page.querySelector('.ls-quote-main'));
  window.addEventListener('resize', updateSticky);

  async function loadPage() {
    setBusy(true);
    listStatus(entries.length ? copy.loading : '');
    try {
      if (!await refreshItems()) await refreshItems();
      listStatus(items.some((item) => item.unavailable) ? copy.remove_unavailable : '');
    } catch {
      items = [];
      page.querySelector('[data-ls-quote-content]').hidden = true;
      listStatus(copy.load_error);
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'ls-quote-text-button';
      retry.textContent = page.querySelector('[data-ls-quote-refresh]').textContent;
      retry.addEventListener('click', () => { if (!busy) loadPage(); });
      page.querySelector('[data-ls-list-status]').append(' ', retry);
    } finally {
      setBusy(false);
    }
  }

  function setQuantity(variantId, quantity, input) {
    const item = items.find((value) => value.variantId === variantId);
    if (!item) return;
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > maxQuantity) {
      if (input) input.value = item.quantity;
      listStatus(copy.invalid_quantity);
      return;
    }
    try {
      saveEntries(entries.map((entry) => entry.variantId === variantId ? { ...entry, quantity } : entry));
      item.quantity = quantity;
      if (input) input.value = quantity;
      listStatus(items.some((value) => value.unavailable) ? copy.remove_unavailable : '');
      updateTotals();
    } catch (error) {
      if (input) input.value = item.quantity;
      listStatus(error.message);
    }
  }

  page.addEventListener('change', (event) => {
    const input = event.target.closest('[data-ls-quote-quantity]');
    if (!input || busy) return;
    const variantId = input.closest('[data-variant-id]').dataset.variantId;
    setQuantity(variantId, input.value.trim() === '' ? NaN : Number(input.value), input);
  });

  page.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.matches('[data-ls-quote-quantity]')) return;
    event.preventDefault();
    event.target.dispatchEvent(new Event('change', { bubbles: true }));
  });

  page.addEventListener('click', (event) => {
    const step = event.target.closest('[data-ls-qty-step]');
    if (step && !busy) {
      const row = step.closest('[data-variant-id]');
      const input = row.querySelector('[data-ls-quote-quantity]');
      const item = items.find((value) => value.variantId === row.dataset.variantId);
      if (item) setQuantity(item.variantId, item.quantity + Number(step.dataset.lsQtyStep), input);
      return;
    }
    const remove = event.target.closest('[data-ls-quote-remove]');
    if (!remove || busy) return;
    const variantId = remove.closest('[data-variant-id]').dataset.variantId;
    const index = items.findIndex((item) => item.variantId === variantId);
    try {
      saveEntries(entries.filter((entry) => entry.variantId !== variantId));
      items = items.filter((item) => item.variantId !== variantId);
      renderItems();
      listStatus(items.some((item) => item.unavailable) ? copy.remove_unavailable : '');
      const removes = page.querySelectorAll('[data-ls-quote-remove]');
      (removes[Math.min(index, removes.length - 1)] || page.querySelector('[data-ls-quote-empty] a'))?.focus();
    } catch (error) {
      listStatus(error.message);
    }
  });

  function clearQuote() {
    try {
      saveEntries([]);
      items = [];
      renderItems();
      listStatus('');
      page.querySelector('[data-ls-quote-empty] a')?.focus();
    } catch (error) {
      listStatus(error.message);
    }
  }

  refreshButton.addEventListener('click', () => { if (!busy) loadPage(); });
  clearButton.addEventListener('click', () => {
    if (!busy && window.confirm(copy.clear_confirm)) clearQuote();
  });

  function readBuyer() {
    return Object.fromEntries(['name', 'email', 'company', 'phone', 'notes'].map((key) => [
      key, form.elements.namedItem(`contact[${key}]`).value.trim(),
    ]));
  }

  function referenceFor(buyer) {
    const fingerprint = JSON.stringify([items, buyer]);
    if (fingerprint !== quoteFingerprint) {
      quoteFingerprint = fingerprint;
      quoteReference = `AIQ-${new Date().toISOString().slice(0, 10).replaceAll('-', '')}-${crypto.randomUUID().slice(0, 6).toUpperCase()}`;
    }
    return quoteReference;
  }

  function emailBody(buyer, reference) {
    const lines = [copy.email_heading, `${copy.reference}: ${reference}`,
      `${copy.date}: ${new Date().toISOString()}`, '',
      ...['name', 'email', 'company', 'phone'].map((key) => `${copy[key]}: ${buyer[key] || '-'}`), '',
    ];
    items.forEach((item, index) => {
      lines.push(`${index + 1}. ${item.title}`,
        ...(item.variant ? [item.variant] : []),
        ...(item.brand ? [`${copy.brand}: ${item.brand}`] : []),
        ...item.specs.map((spec) => `${spec.label}: ${spec.value}`),
        `${copy.sku}: ${item.sku || '-'}`, `${copy.quantity}: ${item.quantity}`,
        ...(stockNote(item) ? [stockNote(item)] : []),
        `${copy.unit_price}: ${money(item.price)}`, `${copy.line_total}: ${money(item.price * item.quantity)}`,
        `https://${config.website}${item.url}`, '');
    });
    lines.push(`${copy.subtotal} (${config.currency}): ${money(items.reduce((sum, item) => sum + item.price * item.quantity, 0))}`,
      '', `${copy.notes}:`, buyer.notes || '-', '', copy.terms);
    return lines.join('\n');
  }

  function captchaReady() {
    return new Promise((resolve, reject) => {
      if (!window.Shopify?.captcha?.protect) {
        resolve();
        return;
      }
      const timeout = setTimeout(() => reject(new Error(copy.downloaded_not_sent)), 20000);
      window.Shopify.captcha.protect(form, () => {
        clearTimeout(timeout);
        resolve();
      });
    });
  }

  // The copy for the store goes through the native contact form, posted in the
  // background so the buyer stays on the page with their PDF. Shopify redirects
  // a good post to return_to; a challenge page, any other page, or the form's
  // error notice means the email did not go.
  async function emailCopy(buyer, reference) {
    const body = emailBody(buyer, reference);
    if (body.length > 60000) return false;
    form.querySelector('[data-ls-quote-reference]').value = reference;
    form.querySelector('[data-ls-quote-email-body]').value = body;
    await captchaReady();
    const data = new URLSearchParams(new FormData(form));
    const expectedPath = new URL(data.get('return_to') || '/', window.location.origin).pathname;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(form.action, {
        method: 'POST', body: data, credentials: 'same-origin', signal: controller.signal,
      });
      const html = await response.text();
      return response.ok && new URL(response.url).pathname === expectedPath
        && !html.includes('ls-quote-form__notice--error');
    } finally {
      clearTimeout(timeout);
    }
  }

  // The totals a buyer downloads must be the live prices, so the list is
  // re-fetched first and the download stops if anything moved.
  async function confirmCurrentPrices() {
    const oldPrices = items.map((item) => item.price).join(',');
    const startRevision = revision;
    if (!await refreshItems() || startRevision !== revision) throw new Error(copy.load_error);
    if (!items.length || items.some((item) => item.unavailable)) throw new Error(copy.remove_unavailable);
    if (oldPrices !== items.map((item) => item.price).join(',')) {
      listStatus(copy.price_changed);
      return null;
    }
    return startRevision;
  }

  function quantitiesValid() {
    return [...page.querySelectorAll('[data-ls-quote-quantity]')].every((input) => input.reportValidity());
  }

  function showReadyBanner(reference) {
    const banner = page.querySelector('[data-ls-quote-banner]');
    page.querySelector('[data-ls-sent-reference]').textContent = copy.sent_reference.replace('[reference]', reference);
    banner.hidden = false;
    banner.focus({ preventScroll: true });
    banner.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  async function downloadQuote() {
    if (busy || !form.reportValidity() || !quantitiesValid()) return;
    setBusy(true);
    setButtonLabel(downloadButton, copy.downloading);
    listStatus('');
    try {
      const startRevision = await confirmCurrentPrices();
      if (startRevision === null) return;
      if (document.fonts) await document.fonts.ready;
      pdfModule ||= import(config.pdfUrl);
      const { createQuotePdf } = await pdfModule.catch((error) => { pdfModule = null; throw error; });
      const buyer = readBuyer();
      const reference = referenceFor(buyer);
      const logo = document.querySelector('.ls-header__logo img');
      const blob = await createQuotePdf({
        ...config, strings: copy, buyer, items, reference, createdAt: new Date(),
        fontFamily: getComputedStyle(form).fontFamily,
        logoUrl: logo?.currentSrc || logo?.src || '',
      });
      if (startRevision !== revision) throw new Error(copy.load_error);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `Annapurna-Quote-${reference}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);

      // A second download of an unchanged quote does not email the team again.
      if (reference !== emailedReference) {
        listStatus(copy.download_sending);
        let emailed = false;
        try {
          emailed = await emailCopy(buyer, reference);
        } catch {
          emailed = false;
        }
        if (!emailed) {
          listStatus(copy.downloaded_not_sent);
          return;
        }
        emailedReference = reference;
        listStatus('');
        showReadyBanner(reference);
        return;
      }
      listStatus(copy.downloaded);
    } catch (error) {
      listStatus([copy.remove_unavailable, copy.load_error].includes(error.message) ? error.message : copy.download_error);
    } finally {
      setButtonLabel(downloadButton, copy.download);
      setBusy(false);
    }
  }

  page.querySelector('[data-ls-quote-new]').addEventListener('click', () => {
    page.querySelector('[data-ls-quote-banner]').hidden = true;
    form.reset();
    clearQuote();
  });
  downloadButton.addEventListener('click', downloadQuote);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    downloadQuote();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      setBusy(false);
      setButtonLabel(downloadButton, copy.download);
      loadPage();
    }
  });
  loadPage();
})();
